import React, { useState, useMemo } from 'react'
import clsx from 'clsx'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

import Button from 'components/Button'
import Carousel, { CarouselItem } from 'components/Carousel'
import TestimonialCard from './TestimonialCard'

import styles from './Signin.module.scss'

import imgAnneSpalter from 'assets/images/testimonial/anne-spalter.jpg'
import imgAnthromorph from 'assets/images/testimonial/anthromorph.jpg'
import imgKatyaGrokhovsky from 'assets/images/testimonial/katya-grokhovsky.jpg'
import imgMattiaCuttini from 'assets/images/testimonial/mattia-cuttini.jpg'
import imgPatrickLichty from 'assets/images/testimonial/patrick-lichty.jpg'
import imgJustinMaynard from 'assets/images/testimonial/justin-maynard.jpg'

const testimonialsData = [
  {
    user: {
      first_name: 'Anne',
      last_name: 'Spalter',
      avatar: imgAnneSpalter
    },
    title: 'Get Inspired',
    message:
      'Not knowing what the outcome will be can inspire new creations. Playform gave me new ideas, new inspirations.'
  },
  {
    user: {
      first_name: 'Katya',
      last_name: 'Grokhovsky',
      avatar: imgKatyaGrokhovsky
    },
    title: 'Create At Scale',
    message:
      'During my Residency with Playform, I was able to produce the largest body of work in a short period of time with ease and accessibility.'
  },
  {
    user: {
      first_name: 'Anthromorph',
      avatar: imgAnthromorph
    },
    title: 'Experiment Quickly',
    message:
      'AI opens a world of infinite design possibilities, drawn and presented to you. It became the perfect tool for experimentation and broadening of concepts.'
  },
  {
    user: {
      first_name: 'Patrick',
      last_name: 'Lichty',
      avatar: imgPatrickLichty
    },
    title: 'Speed Up Process',
    message:
      'What Playform AI does very well is it discriminates, it recognizes patterns. That is what is really revolutionary about using AI.'
  },
  {
    user: {
      first_name: 'Mattia',
      last_name: 'Cuttini',
      avatar: imgMattiaCuttini
    },
    title: 'Sell NFTs',
    message:
      'With Playform I produced an NFT exhibition exclusively on SuperRare which sold out all 12 works.'
  },
  {
    user: {
      first_name: 'Justin',
      last_name: 'Maynard',
      avatar: imgJustinMaynard
    },
    title: 'Visualize Your Concepts',
    message:
      '“Playform has enabled me to pre-visualize and iterate faster without time constraint.”'
  }
]

const CAROUSEL_AUTOPLAY = {
  delay: 5000,
  disableOnInteraction: false
}

type TestimonialCarouselProps = {
  isSmallscreenAbove?: boolean
  isMediumscreenAbove?: boolean
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = props => {
  const { isMediumscreenAbove, isSmallscreenAbove } = props

  const [controlledSwiper, setControlledSwiper] = useState<any>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const testimonialsDataLength = testimonialsData.length
  const testimonialPerSlide = isMediumscreenAbove ? 3 : isSmallscreenAbove ? 2 : 1
  const TestimonialItemsComponent = useMemo(() => {
    return testimonialsData.map((item, index) => {
      return (
        <CarouselItem key={index}>
          <TestimonialCard
            avatar={item.user.avatar}
            name={`${item.user.first_name}${item.user.last_name ? ` ${item.user.last_name}` : ``}`}
            message={item.message}
            title={item.title}
          />
        </CarouselItem>
      )
    })
  }, [])

  return (
    <div className={clsx('relative mx-auto', styles.CarouselTestimonialWrapper)}>
      <Carousel
        autoplay={CAROUSEL_AUTOPLAY}
        className="my-25 px-9"
        slidesPerView={testimonialPerSlide}
        slidesPerGroup={testimonialPerSlide}
        onSwiper={setControlledSwiper}
        onSlideChange={swiper => {
          setActiveIndex(swiper.activeIndex)
        }}
        allowSlideNext
        allowSlidePrev
        allowTouchMove
        grabCursor
      >
        {TestimonialItemsComponent}
      </Carousel>

      <div>
        <div className="absolute inset-y-0 left-0 z-1 flex h-full items-center">
          <Button
            onClick={() => {
              controlledSwiper?.slidePrev()
            }}
            size="small"
            variant="text"
            disabled={activeIndex === 0}
            icon
          >
            <ChevronLeftIcon />
          </Button>
        </div>

        <div className="absolute inset-y-0 right-0 z-1 flex h-full items-center">
          <Button
            onClick={() => {
              controlledSwiper?.slideNext()
            }}
            size="small"
            variant="text"
            disabled={
              testimonialPerSlide > 1
                ? activeIndex + testimonialPerSlide >= testimonialsDataLength
                : activeIndex === testimonialsDataLength - 1
            }
            icon
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default TestimonialCarousel
